import { Box, Typography, ButtonBase } from '@mui/material';
import { useLocales } from 'src/locales';
import { EURO_ZERO, fCurrencyFormat } from 'src/utils/format-number';
import { AgingBucket, IAgingBucketData } from 'src/types';
import { AGING_BUCKET_CONFIG, BUCKET_KEYS } from '../aging-bucket-config';

type Props = {
  buckets: Record<AgingBucket, IAgingBucketData>;
  activeBucket: AgingBucket | null;
  onBucketClick: (bucket: AgingBucket) => void;
};

export default function AgingBucketCards({ buckets, activeBucket, onBucketClick }: Props) {
  const { t, currentLang } = useLocales('operativaDashboard');

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
        gap: 1.5,
      }}
    >
      {BUCKET_KEYS.map((key) => {
        const bucket = buckets?.[key] ?? { amount: 0, count: 0 };
        const { card, labelKey } = AGING_BUCKET_CONFIG[key];
        const isActive = activeBucket === key;

        return (
          <ButtonBase
            key={key}
            onClick={() => onBucketClick(key)}
            sx={{
              display: 'block',
              textAlign: 'left',
              borderRadius: 1,
              p: '12px 16px',
              backgroundColor: card.light.bg,
              color: card.light.text,
              borderTop: isActive ? '2px solid' : '1px solid',
              borderRight: isActive ? '2px solid' : '1px solid',
              borderBottom: isActive ? '2px solid' : '1px solid',
              borderTopColor: isActive ? card.light.text : 'divider',
              borderRightColor: isActive ? card.light.text : 'divider',
              borderBottomColor: isActive ? card.light.text : 'divider',
              borderLeft: '4px solid',
              borderLeftColor: card.light.accent,
              boxShadow: (theme) => (isActive ? theme.shadows[4] : 'none'),
              transition: 'box-shadow 0.2s, border-color 0.2s',
              '[data-color-scheme="dark"] &': {
                backgroundColor: card.dark.bg,
                color: card.dark.text,
                borderTopColor: isActive ? card.dark.text : 'divider',
                borderRightColor: isActive ? card.dark.text : 'divider',
                borderBottomColor: isActive ? card.dark.text : 'divider',
                borderLeftColor: card.dark.accent,
              },
            }}
          >
            <Typography variant="caption" sx={{ color: 'inherit' }}>
              {t(labelKey)}
            </Typography>
            <Typography
              variant="h4"
              component="div"
              sx={{ fontWeight: 'fontWeightMedium', color: 'inherit' }}
            >
              {fCurrencyFormat(bucket.amount, currentLang.value) || EURO_ZERO}
            </Typography>
            <Typography variant="caption" sx={{ color: 'inherit', opacity: 0.7 }}>
              ({bucket.count})
            </Typography>
          </ButtonBase>
        );
      })}
    </Box>
  );
}
